import { Building2, Hotel, TreePine, Users, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function HOACommercialSection() {
  const propertyTypes = [
    {
      icon: Building2,
      title: "HOA Communities",
      description:
        "Comprehensive tree management for gated communities, ensuring aesthetic consistency and safety compliance across all common areas.",
      features: [
        "Common area maintenance",
        "Fire code compliance",
        "Aesthetic uniformity",
        "Emergency response plans",
      ],
    },
    {
      icon: Hotel,
      title: "Hotels & Resorts",
      description:
        "Premium tree care that enhances guest experience while maintaining the immaculate appearance your property demands.",
      features: [
        "Guest safety priority",
        "Minimal disruption scheduling",
        "Luxury aesthetic maintenance",
        "24/7 emergency support",
      ],
    },
    {
      icon: TreePine,
      title: "Golf Courses",
      description:
        "Specialized care for the unique landscaping needs of Paradise Valley and Scottsdale's premier golf communities.",
      features: [
        "Course-specific expertise",
        "Play-friendly scheduling",
        "Hazard tree management",
        "Aesthetic enhancement",
      ],
    },
    {
      icon: Users,
      title: "Commercial Properties",
      description:
        "Professional tree services that protect your investment and create an inviting environment for clients and tenants.",
      features: [
        "Liability reduction",
        "Property value protection",
        "Professional appearance",
        "Flexible scheduling",
      ],
    },
  ];

  return (
    <section
      className="py-28 lg:py-36 bg-[#FDFCF9]"
      data-testid="hoa-commercial-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
            Property Management Solutions
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1628] mb-6">
            HOA, Hotel &
            <br />
            <span className="gradient-text">Commercial Services</span>
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto text-lg">
            Specialized tree care solutions for properties that demand the
            highest standards. We understand the unique requirements of
            large-scale property management.
          </p>
        </div>

        {/* Property Types Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {propertyTypes.map((type, index) => (
            <div
              key={index}
              className="bg-white p-10 border border-[#10B981]/10 hover:border-[#10B981]/40 transition-all duration-300 group hover:shadow-xl hover:shadow-emerald-500/5"
              data-testid={`property-type-${index}`}
            >
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <type.icon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#0A1628] mb-3">
                    {type.title}
                  </h3>
                  <p className="text-[#64748B] mb-5">{type.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {type.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#10B981]" />
                        <span className="text-[#64748B] text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Hotel/Resort Showcase Image */}
        <div className="mb-20 relative overflow-hidden group">
          <img
            src="https://images.unsplash.com/photo-1686471158567-03c65b171492?w=1200&q=80"
            alt="Luxury Arizona resort pool surrounded by professionally maintained palm trees and tropical landscaping"
            className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-xl p-10">
              <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
                Trusted by Arizona's Finest
              </p>
              <h3 className="font-['Cormorant_Garamond'] text-3xl lg:text-4xl font-bold text-white mb-4">
                Premium Tree Care for<br />
                <span className="text-[#D4A853]">Hotels & Resorts</span>
              </h3>
              <p className="text-white/80 mb-6">
                We maintain the pristine landscapes that make Arizona's luxury resorts world-renowned. 
                Our discreet, professional service ensures your guests experience only the finest.
              </p>
              <Button
                asChild
                className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] hover:from-[#B8923A] hover:to-[#9A7B2F] text-[#0A1628] rounded-none px-8 py-5 uppercase tracking-[0.15em] text-sm font-bold"
                data-testid="resort-cta"
              >
                <a href="#contact" className="flex items-center gap-2">
                  Partner With Us
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="bg-gradient-to-r from-[#0A1628] to-[#1E3A5F] p-10 lg:p-16 text-center relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#10B981]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D4A853]/10 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            <h3 className="font-['Cormorant_Garamond'] text-3xl lg:text-4xl font-bold text-white mb-4">
              Need a Custom Solution for Your Property?
            </h3>
            <p className="text-white/70 mb-10 max-w-2xl mx-auto text-lg">
              We create tailored maintenance programs for communities and
              businesses of all sizes. Let's discuss your property's unique needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-gradient-to-r from-[#D4A853] to-[#B8923A] hover:from-[#B8923A] hover:to-[#9A7B2F] text-[#0A1628] rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold shadow-lg shadow-amber-500/20"
                data-testid="hoa-cta-quote"
              >
                <a href="#contact" className="flex items-center gap-2">
                  Request Custom Quote
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold"
                data-testid="hoa-cta-call"
              >
                <a href="tel:4803517775">Call (480) 351-7775</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
